import {
  Alert,
  Button,
  Group,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { DocumentCapture } from "../components/DocumentCapture";
import { ContentCard, LoadingOverlay, PageHeader } from "../components/ui";
import { NAV_ICONS } from "../lib/navCopy";
import {
  COMPANY_KYC_OPTIONAL_SLOTS,
  COMPANY_KYC_REQUIRED_SLOTS,
  COMPANY_KYC_SLOT_LABELS,
  ORG_KYC_COPY,
  orgIsVerified,
} from "../lib/companyKyc";
import { companyApi } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import { kycStatusLabel } from "../lib/kyc";
import type { CompanyType } from "../types/api";

export function CompanyKycPage({ companyType }: { companyType: CompanyType }) {
  const queryClient = useQueryClient();
  const isConsultancy = companyType === "consultancy";
  const kycQuery = useQuery({ queryKey: ["company-kyc"], queryFn: companyApi.kycStatus });
  const [form, setForm] = useState<Record<string, string>>({});
  const [uploadingSlot, setUploadingSlot] = useState<string | null>(null);

  const kyc = kycQuery.data;

  const billing = kyc?.billing_address ?? {};
  const fieldValue = (name: string, fallback = "") => form[name] ?? fallback;
  const setField = (name: string, value: string) => setForm((current) => ({ ...current, [name]: value }));
  const gstin = fieldValue("gstin", kyc?.gstin ?? "");
  const registrationNo = fieldValue("registrationNo", kyc?.registration_no ?? "");
  const companyPan = fieldValue("companyPan");
  const signatoryName = fieldValue("signatoryName", kyc?.authorized_signatory_name ?? "");
  const signatoryPan = fieldValue("signatoryPan");
  const billingLine1 = fieldValue("billingLine1", typeof billing.line1 === "string" ? billing.line1 : "");
  const billingCity = fieldValue("billingCity", typeof billing.city === "string" ? billing.city : "");
  const billingPincode = fieldValue("billingPincode", typeof billing.pincode === "string" ? billing.pincode : "");

  const uploadMutation = useMutation({
    mutationFn: ({ slot, file }: { slot: string; file: File }) => companyApi.uploadKycFile(slot, file),
    onSuccess: (data) => {
      queryClient.setQueryData(["company-kyc"], data);
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Upload failed",
        message: err instanceof ApiError ? err.message : "Could not upload that file",
      });
    },
    onSettled: () => setUploadingSlot(null),
  });

  async function uploadFile(slot: string, file: File) {
    setUploadingSlot(slot);
    await uploadMutation.mutateAsync({ slot, file });
  }

  const submitMutation = useMutation({
    mutationFn: () =>
      companyApi.submitKyc({
        gstin: gstin.trim() || undefined,
        registration_no: registrationNo.trim() || undefined,
        company_pan: companyPan.trim().toUpperCase() || undefined,
        authorized_signatory_name: signatoryName.trim() || undefined,
        authorized_signatory_pan: signatoryPan.trim().toUpperCase() || undefined,
        billing_address: {
          line1: billingLine1.trim() || undefined,
          city: billingCity.trim() || undefined,
          pincode: billingPincode.trim() || undefined,
        },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["company-kyc"], data);
      queryClient.invalidateQueries({ queryKey: ["my-company"] });
      notifications.show({
        color: "green",
        title: ORG_KYC_COPY.submittedTitle,
        message: ORG_KYC_COPY.submittedBody,
      });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Could not submit",
        message: err instanceof ApiError ? err.message : "Check the required fields and try again",
      });
    },
  });

  if (kycQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-280)" />;
  }

  const filled = new Set(kyc?.filled_slots ?? []);
  const verified = orgIsVerified(kyc?.verification_status);
  const locked = verified || kyc?.verification_status === "submitted";

  return (
    <Stack gap="var(--axio-unit-32)">
      <PageHeader
        icon={NAV_ICONS.orgKyc}
        title={isConsultancy ? ORG_KYC_COPY.pageTitleConsultancy : ORG_KYC_COPY.pageTitleClient}
        subtitle={ORG_KYC_COPY.subtitle}
      />

      {kyc?.verification_status === "rejected" && (
        <Alert color="red" title={ORG_KYC_COPY.rejectedTitle}>
          {kyc.verification_notes || ORG_KYC_COPY.rejectedFallback}
        </Alert>
      )}
      {kyc?.verification_status === "submitted" && (
        <Alert color="violet" title={ORG_KYC_COPY.submittedTitle}>
          {ORG_KYC_COPY.submittedBody}
        </Alert>
      )}
      {verified && (
        <Alert color="green" title={ORG_KYC_COPY.verifiedTitle}>
          {ORG_KYC_COPY.verifiedBody}
        </Alert>
      )}

      <Text size="sm" c="dimmed">
        Status: {kycStatusLabel(kyc?.verification_status)}
      </Text>

      {!locked && (
        <>
          <ContentCard maw="var(--axio-unit-640)">
            <Stack gap="md">
              <TextInput
                label={ORG_KYC_COPY.gstinLabel}
                description={ORG_KYC_COPY.gstinHint}
                value={gstin}
                onChange={(e) => setField("gstin", e.currentTarget.value.toUpperCase())}
              />
              <TextInput label={ORG_KYC_COPY.cinLabel} value={registrationNo} onChange={(e) => setField("registrationNo", e.currentTarget.value)} />
              <TextInput
                label={ORG_KYC_COPY.companyPanLabel}
                value={companyPan}
                onChange={(e) => setField("companyPan", e.currentTarget.value.toUpperCase())}
                description={kyc?.company_pan_set ? ORG_KYC_COPY.companyPanOnFile : undefined}
              />
              <TextInput label={ORG_KYC_COPY.signatoryNameLabel} value={signatoryName} onChange={(e) => setField("signatoryName", e.currentTarget.value)} />
              <TextInput
                label={ORG_KYC_COPY.signatoryPanLabel}
                value={signatoryPan}
                onChange={(e) => setField("signatoryPan", e.currentTarget.value.toUpperCase())}
                description={kyc?.authorized_signatory_pan_set ? ORG_KYC_COPY.signatoryPanOnFile : undefined}
              />
              <Textarea label={ORG_KYC_COPY.billingLine1Label} value={billingLine1} onChange={(e) => setField("billingLine1", e.currentTarget.value)} />
              <TextInput label={ORG_KYC_COPY.billingCityLabel} value={billingCity} onChange={(e) => setField("billingCity", e.currentTarget.value)} />
              <TextInput label={ORG_KYC_COPY.billingPincodeLabel} value={billingPincode} onChange={(e) => setField("billingPincode", e.currentTarget.value)} />
            </Stack>
          </ContentCard>

          <Stack gap="md" maw="var(--axio-unit-640)">
            {COMPANY_KYC_REQUIRED_SLOTS.map((slot) => (
              <DocumentCapture
                key={slot}
                label={COMPANY_KYC_SLOT_LABELS[slot]}
                facing="environment"
                filled={filled.has(slot)}
                uploading={uploadingSlot === slot}
                onFile={(file) => uploadFile(slot, file)}
              />
            ))}
            {COMPANY_KYC_OPTIONAL_SLOTS.map((slot) => (
              <DocumentCapture
                key={slot}
                label={`${COMPANY_KYC_SLOT_LABELS[slot]} ${ORG_KYC_COPY.optionalSuffix}`}
                facing="environment"
                filled={filled.has(slot)}
                uploading={uploadingSlot === slot}
                onFile={(file) => uploadFile(slot, file)}
              />
            ))}
          </Stack>

          <Group>
            <Button color="violet" loading={submitMutation.isPending} onClick={() => submitMutation.mutate()}>
              {ORG_KYC_COPY.submitCta}
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );
}
